import React, { useState } from 'react';
import { 
  FaUsers, FaChevronDown, FaChevronUp, FaBoxOpen, FaSearch, FaTags, FaShoppingBag 
} from 'react-icons/fa';

/* ================= TYPES ================= */

interface Product {
  name: string;
  quantity: number;
  image: string;
  price: number;
}

interface SubCategory {
  name: string;
  products: Product[];
}

interface Category {
  name: string;
  subcategories: SubCategory[];
}

interface Seller {
  id: number;
  name: string;
  totalSold: number;
  revenue: number;
  categories: Category[];
}

/* ================= MOCK DATA (Can be replaced with API) ================= */

const sellersData: Seller[] = [
  {
    id: 1,
    name: 'Vikas Traders',
    totalSold: 520,
    revenue: 125000,
    categories: [
      {
        name: 'Home Supplier',
        subcategories: [
          {
            name: 'Bags & Cases',
            products: [
              { name: 'Leather Handbag', quantity: 120, price: 1500, image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?auto=format&fit=crop&q=80&w=200' },
              { name: 'Travel Trolley', quantity: 80, price: 3500, image: 'https://images.unsplash.com/photo-1565084888279-aca607ecce0c?auto=format&fit=crop&q=80&w=200' }
            ]
          }
        ]
      },
      {
        name: 'Electronics',
        subcategories: [
          {
            name: 'Accessories',
            products: [
              { name: 'Wireless Buds', quantity: 200, price: 999, image: 'https://images.unsplash.com/photo-1572569028738-411a29630962?auto=format&fit=crop&q=80&w=200' },
              { name: 'Smart Watch', quantity: 120, price: 2499, image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&q=80&w=200' }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 2,
    name: 'Tech World',
    totalSold: 340,
    revenue: 85000,
    categories: [
      {
        name: 'Gadgets',
        subcategories: [
          {
            name: 'Mobile Phones',
            products: [
              { name: 'Smartphone X', quantity: 140, price: 15000, image: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?auto=format&fit=crop&q=80&w=200' },
              { name: 'Power Bank', quantity: 200, price: 800, image: 'https://images.unsplash.com/photo-1609091839311-d5365f9ff1c5?auto=format&fit=crop&q=80&w=200' }
            ]
          }
        ]
      }
    ]
  }
];

const OrdersPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSeller, setExpandedSeller] = useState<number | null>(null);

  const toggleSeller = (id: number) => {
    setExpandedSeller(expandedSeller === id ? null : id);
  };

  const filteredSellers = sellersData.filter(seller => 
    seller.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-20 font-sans animate-fade-in-up">
      <div className="max-w-[1600px] mx-auto space-y-8">
        
        {/* 1. HEADER */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-black text-gray-900 flex items-center gap-3">
              <span className="p-2 bg-[#006d5b]/10 text-[#006d5b] rounded-lg">
                <FaShoppingBag size={20} />
              </span>
              Order & Sales Analytics
            </h1>
            <p className="text-gray-500 text-xs font-bold mt-1 ml-1">
              Track product sales across all sellers.
            </p>
          </div>
          
          <div className="relative group w-full md:w-72">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#006d5b]" />
            <input 
              type="text" 
              placeholder="Search seller..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl py-2.5 pl-10 pr-4 text-xs font-bold focus:ring-2 focus:ring-[#006d5b]/20 focus:border-[#006d5b] outline-none transition-all"
            />
          </div>
        </div>

        {/* 2. STATS OVERVIEW (Optional) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
           <div className="bg-[#006d5b] text-white p-6 rounded-2xl shadow-lg shadow-[#006d5b]/20 relative overflow-hidden">
              <p className="text-xs font-bold opacity-80 uppercase tracking-widest">Total Revenue</p>
              <h3 className="text-3xl font-black mt-1">₹ 2.1L</h3>
              <div className="absolute right-0 bottom-0 opacity-10"><FaShoppingBag size={80}/></div>
           </div>
           <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Products Sold</p>
              <h3 className="text-3xl font-black text-gray-900 mt-1">860</h3>
           </div>
           <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Active Sellers</p>
              <h3 className="text-3xl font-black text-gray-900 mt-1">{sellersData.length}</h3>
           </div>
        </div>

        {/* 3. SELLERS LIST (Accordion) */}
        <div className="space-y-4">
          {filteredSellers.map((seller) => (
            <div key={seller.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300">
              
              {/* Seller Header Row */}
              <div 
                onClick={() => toggleSeller(seller.id)}
                className={`p-6 flex flex-col md:flex-row items-center justify-between cursor-pointer hover:bg-gray-50/50 transition-colors gap-4 ${expandedSeller === seller.id ? 'bg-gray-50' : ''}`}
              >
                 <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="w-12 h-12 rounded-xl bg-[#006d5b]/10 text-[#006d5b] flex items-center justify-center font-black text-lg border border-[#006d5b]/20">
                       {seller.name.charAt(0)}
                    </div>
                    <div>
                       <h3 className="text-lg font-black text-gray-900">{seller.name}</h3>
                       <p className="text-xs text-gray-500 font-bold flex items-center gap-2">
                          <FaBoxOpen size={10} /> {seller.categories.length} Categories
                       </p>
                    </div>
                 </div>

                 <div className="flex items-center gap-8 w-full md:w-auto justify-between md:justify-end">
                    <div className="text-right">
                       <p className="text-[10px] text-gray-400 font-black uppercase">Total Sold</p>
                       <p className="text-sm font-black text-gray-900">{seller.totalSold} Units</p>
                    </div>
                    <div className="text-right">
                       <p className="text-[10px] text-gray-400 font-black uppercase">Revenue</p>
                       <p className="text-sm font-black text-[#006d5b]">₹ {seller.revenue.toLocaleString()}</p>
                    </div>
                    <div className={`transition-transform duration-300 ${expandedSeller === seller.id ? 'rotate-180' : ''}`}>
                       <FaChevronDown className="text-gray-400" />
                    </div>
                 </div>
              </div>

              {/* Expanded Content */}
              {expandedSeller === seller.id && (
                <div className="p-6 border-t border-gray-100 bg-[#FAFAFA] space-y-8 animate-fade-in">
                   {seller.categories.map((cat, idx) => (
                     <div key={idx} className="relative">
                        {/* Vertical Line for Tree Structure */}
                        <div className="absolute top-4 bottom-0 left-[9px] w-[2px] bg-gray-200"></div>

                        <div className="flex items-center gap-3 mb-4">
                           <div className="w-5 h-5 rounded-full bg-[#006d5b] border-4 border-white shadow-sm z-10"></div>
                           <h4 className="text-sm font-black text-gray-800 uppercase tracking-widest">{cat.name}</h4>
                        </div>

                        <div className="ml-8 space-y-6">
                           {cat.subcategories.map((sub, sIdx) => (
                              <div key={sIdx}>
                                 <h5 className="text-xs font-bold text-gray-500 mb-3 flex items-center gap-2">
                                    <FaTags className="text-gray-300"/> {sub.name}
                                 </h5>
                                 
                                 {/* Product Grid */}
                                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                    {sub.products.map((prod, pIdx) => (
                                       <div key={pIdx} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-[#006d5b]/30 transition-all group">
                                          <div className="h-32 bg-gray-50 overflow-hidden relative">
                                             <img src={prod.image} alt={prod.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                             <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded">
                                                Sold: {prod.quantity}
                                             </div>
                                          </div>
                                          <div className="p-3">
                                             <h6 className="font-bold text-gray-900 text-xs truncate" title={prod.name}>{prod.name}</h6>
                                             <p className="text-[11px] font-bold text-[#006d5b] mt-1">₹ {prod.price.toLocaleString()}</p>
                                          </div>
                                       </div>
                                    ))}
                                 </div>
                              </div>
                           ))}
                        </div>
                     </div>
                   ))}
                </div>
              )}

            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default OrdersPage;